import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ShieldCheck } from "lucide-react";

interface Props {
  factorId: string;
  onVerified: () => void;
  onCancel: () => void;
}

export function MFAChallenge({ factorId, onVerified, onCancel }: Props) {
  const { toast } = useToast();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setLoading(true);
    try {
      const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId });
      if (cErr) throw cErr;
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (vErr) throw vErr;
      onVerified();
    } catch (e: any) {
      toast({ title: "Incorrect code", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    await supabase.auth.signOut();
    onCancel();
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <ShieldCheck className="h-4 w-4" />
        Enter the 6-digit code from your authenticator app.
      </div>
      <div className="space-y-2">
        <Label htmlFor="challenge-code">Verification code</Label>
        <Input
          id="challenge-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="123456"
        />
      </div>
      <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
        {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
        Verify
      </Button>
      <Button type="button" variant="ghost" className="w-full" onClick={cancel}>
        Cancel and sign out
      </Button>
    </form>
  );
}
